<template>
  <v-app>
    <template v-if="isAdminRoute">
      <v-navigation-drawer
        v-if="showAdminChrome"
        v-model="adminDrawer"
        app
        color="grey-darken-4"
        theme="dark"
        :permanent="$vuetify.display.mdAndUp"
        :rail="$vuetify.display.mdAndUp"
        expand-on-hover
      >
        <v-list density="compact" nav>
          <v-tooltip
            v-for="item in filteredAdminNavItems"
            :key="item.to"
            :text="item.title"
            location="end"
            :disabled="!$vuetify.display.mdAndUp"
          >
            <template #activator="{ props }">
              <v-list-item v-bind="props" :to="item.to" :prepend-icon="item.icon" :title="item.title" />
            </template>
          </v-tooltip>
        </v-list>
      </v-navigation-drawer>

      <v-app-bar v-if="showAdminChrome" color="primary" density="compact">
        <v-app-bar-nav-icon v-if="!$vuetify.display.mdAndUp" @click="adminDrawer = !adminDrawer" />
        <v-app-bar-title>Digital Products Admin</v-app-bar-title>
        <v-spacer />
        <span class="text-body-2 d-none d-sm-block text-truncate" style="max-width: 240px">
          {{ authStore.user?.email }} · {{ getRoleLabel(authStore.user?.role ?? 100) }}
        </span>
        <v-tooltip text="Logout" location="bottom">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon="mdi-logout" variant="text" density="comfortable" @click="handleAdminLogout" />
          </template>
        </v-tooltip>
      </v-app-bar>

      <v-main>
        <v-container v-if="showAdminChrome" fluid>
          <v-breadcrumbs v-if="adminBreadcrumbs.length" :items="adminBreadcrumbs" class="px-0 pb-4">
            <template #divider><v-icon icon="mdi-chevron-right" /></template>
          </v-breadcrumbs>
          <router-view />
        </v-container>
        <router-view v-else />
      </v-main>
    </template>

    <template v-else>
      <v-app-bar color="primary" density="comfortable">
        <v-app-bar-nav-icon class="d-md-none" @click="customerDrawer = !customerDrawer" />
        <v-app-bar-title>
          <router-link to="/" class="text-white text-decoration-none">{{ storeName }}</router-link>
        </v-app-bar-title>
        <v-spacer />

        <div class="d-none d-md-flex align-center ga-2">
          <v-btn variant="text" to="/catalog">Catalog</v-btn>
          <v-btn v-if="authStore.isAdmin" variant="text" to="/office">Admin</v-btn>
          <v-btn v-if="authStore.isUserx" icon to="/cart">
            <v-badge :content="cartStore.count" color="error" :model-value="cartStore.count > 0">
              <v-icon>mdi-cart</v-icon>
            </v-badge>
          </v-btn>
          <template v-if="authStore.isUserx">
            <v-btn variant="text" to="/orders">Orders</v-btn>
            <v-menu>
              <template #activator="{ props }">
                <v-btn v-bind="props" variant="text"><v-icon start>mdi-account</v-icon>{{ authStore.user?.email }}</v-btn>
              </template>
              <v-list>
                <v-list-item to="/account" prepend-icon="mdi-wallet">Credit balance</v-list-item>
                <v-list-item @click="handleCustomerLogout"><v-list-item-title>Logout</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template v-else>
            <v-btn variant="text" to="/login">Login</v-btn>
            <v-btn variant="outlined" to="/register" class="ml-2">Register</v-btn>
          </template>
        </div>
      </v-app-bar>

      <v-navigation-drawer v-model="customerDrawer" temporary>
        <v-list>
          <v-list-item to="/" prepend-icon="mdi-home" title="Home" @click="customerDrawer = false" />
          <v-list-item to="/catalog" prepend-icon="mdi-file-tree" title="Catalog" @click="customerDrawer = false" />
          <v-list-item v-if="authStore.isAdmin" to="/office" prepend-icon="mdi-shield-account" title="Admin" @click="customerDrawer = false" />
          <template v-if="authStore.isUserx">
            <v-list-item to="/cart" prepend-icon="mdi-cart" title="Cart" @click="customerDrawer = false">
              <template #append><v-chip v-if="cartStore.count > 0" size="small" color="error">{{ cartStore.count }}</v-chip></template>
            </v-list-item>
            <v-list-item to="/orders" prepend-icon="mdi-clipboard-list" title="Orders" @click="customerDrawer = false" />
            <v-list-item to="/account" prepend-icon="mdi-wallet" title="Credit balance" @click="customerDrawer = false" />
            <v-divider class="my-2" />
            <v-list-item prepend-icon="mdi-account" :title="authStore.user?.email || 'Account'" />
            <v-list-item prepend-icon="mdi-logout" title="Logout" @click="handleCustomerLogout" />
          </template>
          <template v-else>
            <v-list-item to="/login" prepend-icon="mdi-login" title="Login" @click="customerDrawer = false" />
            <v-list-item to="/register" prepend-icon="mdi-account-plus" title="Register" @click="customerDrawer = false" />
          </template>
        </v-list>
      </v-navigation-drawer>

      <v-main><router-view /></v-main>

      <v-footer app color="grey-lighten-3" class="text-center">
        <v-col class="text-caption">
          <template v-if="storeConfig">
            <div v-if="storeConfig.contact_email || storeConfig.contact_phone || storeConfig.contact_telegram">
              <span v-if="storeConfig.contact_email">Email: {{ storeConfig.contact_email }}</span>
              <span v-if="storeConfig.contact_phone" class="mx-2">| Phone: {{ storeConfig.contact_phone }}</span>
              <span v-if="storeConfig.contact_telegram">| Telegram: {{ storeConfig.contact_telegram }}</span>
            </div>
            <div v-else>© {{ currentYear }} {{ storeName }} — Digital Product Store</div>
          </template>
          <template v-else>© {{ currentYear }} {{ storeName }} — Digital Product Store</template>
        </v-col>
      </v-footer>
    </template>
  </v-app>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTheme } from 'vuetify'
import {
  getRoleLabel,
  useAuthStore,
  useCartStore,
  useSessionSync,
  useStoreSettingsStore,
} from '@dpweb/shared'

const router = useRouter()
const route = useRoute()
const theme = useTheme()
const authStore = useAuthStore()
const cartStore = useCartStore()
const storeSettings = useStoreSettingsStore()
const storeConfig = computed(() => storeSettings.settings)
const customerDrawer = ref(false)
const adminDrawer = ref(true)
const currentYear = new Date().getFullYear()

const storeName = computed(() => {
  const configuredName = storeConfig.value?.name?.trim()
  return configuredName && configuredName.toLowerCase() !== 'dpweb'
    ? configuredName
    : 'Digital Products'
})
const isAdminRoute = computed(() => route.meta.admin === true)
const showAdminChrome = computed(() => isAdminRoute.value && authStore.isLoggedIn && authStore.isAdmin)

watch(isAdminRoute, isAdmin => {
  theme.global.name.value = isAdmin ? 'dark' : 'light'
}, { immediate: true })

const adminNavItems = [
  { title: 'Dashboard', to: '/office', icon: 'mdi-view-dashboard', requiresRole: 100 },
  { title: 'Change Password', to: '/office/change-password', icon: 'mdi-lock-reset', requiresRole: 100 },
  { title: 'Items', to: '/office/items', icon: 'mdi-file-tree', requiresRole: 1 },
  { title: 'Stocks', to: '/office/stocks', icon: 'mdi-package-variant', requiresRole: 1 },
  { title: 'Customers', to: '/office/customers', icon: 'mdi-account-group', requiresRole: 1 },
  { title: 'Orders', to: '/office/orders', icon: 'mdi-cart', requiresRole: 1 },
  { title: 'Mails', to: '/office/mails', icon: 'mdi-email', requiresRole: 1 },
  { title: 'Setting', to: '/office/setting', icon: 'mdi-cog', requiresRole: 1 },
  { title: 'Users', to: '/office/users', icon: 'mdi-shield-account', requiresRole: 2 },
]

const filteredAdminNavItems = computed(() => {
  const role = authStore.user?.role ?? 100
  return adminNavItems.filter(item => role <= item.requiresRole)
})

const adminBreadcrumbs = computed(() => {
  if (route.path === '/office') return []
  const item = adminNavItems.find(nav => nav.to === route.path)
  const segments = route.path.split('/').filter(Boolean)
  const finalLabel = item?.title ?? segments[segments.length - 1]?.replace(/-/g, ' ')
  return [
    { title: 'Dashboard', to: '/office', disabled: false },
    { title: finalLabel ? finalLabel.replace(/\b\w/g, letter => letter.toUpperCase()) : 'Admin', to: route.path, disabled: true },
  ]
})

useSessionSync({
  onInvalidated: () => {
    if (route.meta.requiresAdmin) router.push({ name: 'admin-login' })
    else if (route.meta.requiresAuth) router.push({ name: 'login' })
  },
})

onMounted(() => {
  storeSettings.fetchPublic().catch(error => {
    console.error('Store settings could not be loaded:', error)
  })
  if (authStore.isUserx) cartStore.fetchCart()
})

async function handleAdminLogout() {
  await authStore.logout()
  router.push({ name: 'admin-login' })
}

async function handleCustomerLogout() {
  await authStore.logout()
  cartStore.$reset()
  customerDrawer.value = false
  router.push('/')
}
</script>
